import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Chip,
  Tooltip,
  Link,
  Pagination,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  Refresh as RefreshIcon,
  FavoriteBorder as LikeIcon,
  ChatBubbleOutline as CommentIcon,
  Share as ShareIcon,
  Visibility as ImpressionIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformColor, getPlatformName, platformPostUrl } from '../config/platforms';
import { AudienceTotals } from '../components/AudienceTotals';
import { AdSpend } from '../components/AdSpend';
import { Boosts } from '../components/Boosts';
import { BestPerformers } from '../components/BestPerformers';
import { OrganicVsPaid } from '../components/OrganicVsPaid';
import { XCredits } from '../components/XCredits';
import { SectionCard } from '../components/SectionCard';
import { formatMoney } from '../utils/money';
import { StatsSkeleton, SectionSkeleton } from '../components/SectionSkeleton';
import { METRIC_COLORS } from '../config/chartTheme';
import { FollowerHistory } from '../components/FollowerHistory';
import { AudienceDemographics } from '../components/AudienceDemographics';
import { PostImpact } from '../components/PostImpact';
import { StatsExport } from '../components/StatsExport';
import {
  EngagementOverTime,
  PostingVolume,
  PlatformComparison,
} from '../components/EngagementOverTime';
import { StatTile } from '../components/StatTile';
import { formatDate, withPaid, withBoost, sourceOf } from '../utils/statsFormat';
import type { Summary } from '../types/stats';

// The tables bring in MUI DataGrid (~100 KB), so they load only when shown rather than in the
// bundle every page downloads
const PostsTable = React.lazy(() => import('../components/PostsTable').then(m => ({ default: m.PostsTable })));

const API_URL = process.env.REACT_APP_API_URL || '';

/** Posts shown per page in Engagement by post. The list is otherwise unbounded. */
const POSTS_PER_PAGE = 15;

/**
 * Stats is split into tabs (2026-10-17). One page had grown past a dozen sections — engagement,
 * ads, boosts, audience, demographics, per-post — and a jump menu was a workaround for the
 * length. Each tab answers one question; the chosen tab lives in the URL (?tab=ads) so a link
 * opens it, and only the open tab's sections load.
 */
const STATS_TABS = [
  { id: 'overview', label: 'Overview', short: 'Overview' },
  { id: 'ads', label: 'Ads & Boosts', short: 'Ads' },
  // Admins only: the credit belongs to the X developer account, not to a workspace
  { id: 'xcredit', label: 'X Credit', short: 'X Credit', adminOnly: true },
  { id: 'audience', label: 'Audience', short: 'Audience' },
  { id: 'posts', label: 'Posts', short: 'Posts' },
] as const;
type StatsTab = typeof STATS_TABS[number]['id'];

export const Analytics: React.FC = () => {
  const { token, user } = useAuth();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [days, setDays] = useState(30);
  /**
   * Narrow "Engagement by post" to the posts carrying one metric.
   *
   * The tiles above say "1 comment, all from X" and the list below has every post published
   * in the window — so finding which of thirty-one posts holds that one comment meant reading
   * all of them. Clicking through from the number is the obvious move and there was nowhere
   * to click.
   */
  const [postFilter, setPostFilter] = useState<'all' | 'likes' | 'comments' | 'shares' | 'impressions'>('all');
  const [postPage, setPostPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = STATS_TABS.filter(t => !('adminOnly' in t) || user?.role === 'admin');
  const tab: StatsTab = (tabs.find(t => t.id === searchParams.get('tab'))?.id) || 'overview';
  const setTab = (next: StatsTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab'); else params.set('tab', next);
    setSearchParams(params, { replace: false });
  };
  /** From a tile's "Show the posts": the Posts tab, already filtered to that metric. */
  const drill = (metric: 'likes' | 'comments' | 'shares' | 'impressions') => {
    setPostFilter(metric);
    setPostPage(0);
    setTab('posts');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // Bumped after a LinkedIn report import so the Boosts list reloads with it
  const [boostsKey, setBoostsKey] = useState(0);

  const load = useCallback(async (range: number) => {
    try {
      const response = await fetch(`${API_URL}/api/analytics?days=${range}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        setData(await response.json());
      } else {
        setError('Failed to load analytics');
      }
    } catch (err) {
      setError('Network error loading analytics');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load(days);
  }, [load, days]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    setInfo('');

    try {
      const response = await fetch(`${API_URL}/api/analytics/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();

      if (response.ok) {
        setInfo(result.message);
        load(days);
      } else {
        setError(result.error || 'Refresh failed');
      }
    } catch (err) {
      setError('Network error refreshing analytics');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <StatsSkeleton />
    );
  }

  // Filtered and, when filtering, sorted so the posts carrying the metric come first.
  // Boosted figures count, as they do in the tiles: a comment that came through a boost made
  // the Comments tile say 2 while "With comments" listed one post.
  const visiblePosts = (data?.top_posts || [])
    .filter(p => postFilter === 'all' || withBoost(p, postFilter) > 0)
    .sort((a, b) => postFilter === 'all' ? 0 : withBoost(b, postFilter) - withBoost(a, postFilter));

  // Paged in the browser rather than the server: the window's posts are already loaded for
  // the totals above, so asking again per page would be a second trip for data in hand.
  const postPages = Math.ceil(visiblePosts.length / POSTS_PER_PAGE);
  // Clamped, not just stored: switching filter or shortening the window can leave the current
  // page beyond the end, which would otherwise render an empty list with no way back.
  const currentPostPage = Math.min(postPage, Math.max(0, postPages - 1));
  const pagedPosts = visiblePosts.slice(
    currentPostPage * POSTS_PER_PAGE,
    currentPostPage * POSTS_PER_PAGE + POSTS_PER_PAGE
  );

  const totals = data?.totals;
  const maxEngagement = Math.max(1, ...(data?.by_platform || []).map(p => p.likes + p.comments + p.shares));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1">Stats</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_, value) => value && setDays(value)}
          >
            <ToggleButton value={7}>7d</ToggleButton>
            <ToggleButton value={30}>30d</ToggleButton>
            <ToggleButton value={90}>90d</ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          {/* Its own date range rather than the page's: exporting a specific month for a
              report should not mean changing what is on screen to get it. */}
          <StatsExport />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Engagement on posts published in the last {days} days. Metrics update hourly;
        last fetched {formatDate(data?.last_fetched_at || null)}.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 3 }} onClose={() => setInfo('')}>{info}</Alert>}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant={narrow ? 'fullWidth' : 'scrollable'}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minWidth: { xs: 0, sm: 90 }, px: { xs: 1, sm: 2 } } }}
      >
        {tabs.map(t => <Tab key={t.id} value={t.id} label={narrow ? t.short : t.label} />)}
      </Tabs>

      {tab === 'overview' && (
        <>
          {/* Two per row on a phone: one per row made the four tiles four screens of scrolling */}
          <Box id="overview" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 3 }, mb: 3 }}>
        <StatTile label="Likes / reactions" value={(totals?.likes || 0) + (totals?.paid_likes || 0)}
          icon={<LikeIcon />} color={METRIC_COLORS.likes}
          source={withPaid(sourceOf(data?.by_platform || [], 'likes'), totals?.likes, totals?.paid_likes)}
          onDrill={() => drill('likes')} />
        <StatTile label="Comments" value={(totals?.comments || 0) + (totals?.paid_comments || 0)}
          icon={<CommentIcon />} color={METRIC_COLORS.comments}
          source={withPaid(sourceOf(data?.by_platform || [], 'comments'), totals?.comments, totals?.paid_comments)}
          onDrill={() => drill('comments')} />
        <StatTile label="Shares / reposts" value={(totals?.shares || 0) + (totals?.paid_shares || 0)}
          icon={<ShareIcon />} color={METRIC_COLORS.shares}
          source={withPaid(sourceOf(data?.by_platform || [], 'shares'), totals?.shares, totals?.paid_shares)}
          onDrill={() => drill('shares')} />
        <StatTile label="Impressions" value={(totals?.impressions || 0) + (totals?.paid_impressions || 0)}
          icon={<ImpressionIcon />} color={METRIC_COLORS.impressions}
          source={withPaid(sourceOf(data?.by_platform || [], 'impressions'), totals?.impressions, totals?.paid_impressions)}
          onDrill={() => drill('impressions')} />
          </Box>

          {totals?.measured === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No engagement data yet. Metrics appear once posts have been published and the
              platforms report figures for them — press Refresh to fetch now, or wait for the
              hourly update. Note LinkedIn does not expose impressions for personal posts, and
              X impressions require elevated access.
            </Alert>
          )}

          <EngagementOverTime timeline={data?.timeline || []} />
          <BestPerformers posts={data?.top_posts || []} days={days} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3 }}>
            <PostingVolume timeline={data?.timeline || []} />
            <PlatformComparison
              byPlatform={data?.by_platform || []}
              platformName={getPlatformName}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3 }}>
            <SectionCard id="by-platform" title="By Platform">
            {(data?.by_platform.length || 0) === 0 ? (
              <Typography variant="body2" color="text.secondary">Nothing measured yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                {data?.by_platform.map(row => {
                  const engagement = row.likes + row.comments + row.shares;
                  return (
                    <Box key={row.platform}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getPlatformName(row.platform)}
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {row.posts} post{row.posts === 1 ? '' : 's'}
                          </Typography>
                        </Typography>
                        <Typography variant="body2">{engagement.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ height: 8, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{
                          width: `${(engagement / maxEngagement) * 100}%`,
                          height: '100%',
                          bgcolor: getPlatformColor(row.platform),
                        }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {row.likes.toLocaleString()} likes · {row.comments.toLocaleString()} comments
                        · {row.shares.toLocaleString()} shares
                        {row.impressions > 0 && ` · ${row.impressions.toLocaleString()} impressions`}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            </SectionCard>

            <SectionCard title="Publishing">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
              {([
                ['Published', data?.publishing.published || 0, 'success.main'],
                ['Scheduled', data?.publishing.scheduled || 0, 'info.main'],
                ['Drafts', data?.publishing.drafts || 0, 'text.secondary'],
                ['Failed', data?.publishing.failed || 0, 'error.main'],
              ] as const).map(([label, value, color]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Typography variant="body2">{label}</Typography>
                  <Typography variant="body2" sx={{ color, fontWeight: 600 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
            </SectionCard>
          </Box>
        </>
      )}

      {tab === 'ads' && (
        <>
          {/* Only when something was actually boosted — an account that never advertises should
              not carry an empty spend panel. */}
          {(totals?.promoted_posts || 0) > 0 ? (
            <SectionCard
              id="boosted"
              title="From Boosted Posts"
              description="The advertising portion of the totals on Overview, shown on its own so the spend can be judged against what it bought."
            >
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {([
                  [(totals?.paid_likes || 0).toLocaleString(), 'likes'],
                  [(totals?.paid_impressions || 0).toLocaleString(), 'impressions'],
                  [formatMoney(totals?.paid_spend || 0), 'spent'],
                  [String(totals?.promoted_posts || 0), `post${(totals?.promoted_posts || 0) === 1 ? '' : 's'} boosted`],
                  ...((totals?.paid_likes || 0) > 0 && (totals?.paid_spend || 0) > 0
                    ? [[formatMoney((totals?.paid_spend || 0) / (totals?.paid_likes || 1)), 'per like']]
                    : []),
                ] as [string, string][]).map(([value, label]) => (
                  <Box key={label}>
                    <Typography variant="h6">{value}</Typography>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                ))}
              </Box>
            </SectionCard>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>No boosted posts were published in the last {days} days.</Alert>
          )}

          {/* Not gated on promoted_posts: that counts boosted posts *published* in the window, and a
              boost still running on an older post belongs here too, as it does in Ad Spend. */}
          <OrganicVsPaid byPlatform={data?.by_platform || []} days={days} />

          <AdSpend days={days} onImported={() => { load(days); setBoostsKey(k => k + 1); }} />

          <Boosts days={days} refreshKey={boostsKey} />
        </>
      )}

      {tab === 'xcredit' && <XCredits days={days} />}

      {tab === 'audience' && (
        <>
          <AudienceTotals days={days} />
          {/* The question people open Audience to ask: did a post move followers. Above the
              per-account charts because it needs no expanding to be useful. */}
          <PostImpact days={Math.max(days, 90)} />
          <FollowerHistory days={Math.max(days, 90)} />
          {/* Who those followers are, where the platform will say. Aggregate only. */}
          <AudienceDemographics days={Math.max(days, 90)} />
        </>
      )}

      {tab === 'posts' && (
        <SectionCard
          id="engagement-by-post"
          title="Engagement By Post"
          description="Every post that published in this window, per platform, so a total can be traced back to where it came from. Click a post to open it on the platform."
        >
          {/* Filtering is what turns "1 comment, all from X" into the post that holds it.
              Sorted by the chosen metric so the largest contributor is first. */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={postFilter}
            onChange={(_, v) => { if (v) { setPostFilter(v); setPostPage(0); } }}
            sx={{ flexWrap: 'wrap', mb: 1 }}
          >
            <ToggleButton value="all">All posts</ToggleButton>
            <ToggleButton value="likes">With likes</ToggleButton>
            <ToggleButton value="comments">With comments</ToggleButton>
            <ToggleButton value="shares">With shares</ToggleButton>
            <ToggleButton value="impressions">With impressions</ToggleButton>
          </ToggleButtonGroup>

          {postFilter !== 'all' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {visiblePosts.length === 0
                ? `No post in this window has any ${postFilter}.`
                : `${visiblePosts.length} post${visiblePosts.length === 1 ? '' : 's'} with ${postFilter}, most first.`}
            </Typography>
          )}
          {(data?.top_posts.length || 0) === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nothing published in this window yet.
            </Typography>
          ) : !narrow ? (
            // Sortable, searchable and exportable on wider screens; the list below on a phone
            <React.Suspense fallback={<SectionSkeleton frameless rows={6} />}>
              <PostsTable posts={visiblePosts} />
            </React.Suspense>
          ) : (
            <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
              {pagedPosts.map((post, index) => {
                const url = platformPostUrl(post.platform, post.platform_post_id, post.permalink);
                return (
                  <Box
                    key={`${post.id}-${post.platform}`}
                    sx={{
                      display: 'flex',
                      // A 90px chip, the post text and a 168px figures column do not fit
                      // side by side on a phone. Stacked there, side by side from sm up.
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 0.75, sm: 2 },
                      alignItems: { xs: 'stretch', sm: 'flex-start' },
                      pb: 1.5,
                      borderBottom: index < visiblePosts.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                    }}
                  >
                    <Chip
                      size="small"
                      label={getPlatformName(post.platform)}
                      sx={{ bgcolor: getPlatformColor(post.platform), color: '#fff', minWidth: 90 }}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {url ? (
                          <Link href={url} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">
                            {post.content}
                          </Link>
                        ) : post.content}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(post.published_at)}
                      </Typography>
                    </Box>
                    {/* Inline rather than a tooltip: the whole point is being able to see
                        where a number came from without hunting for it. */}
                    <Box sx={{
                      textAlign: { xs: 'left', sm: 'right' },
                      whiteSpace: 'nowrap',
                      minWidth: { xs: 0, sm: 168 },
                    }}>
                      {post.measured === 0 ? (
                        <Tooltip title="Metrics have not been fetched for this post yet. The hourly refresh only covers posts published in the last 30 days.">
                          <Typography variant="caption" color="text.secondary">
                            not measured
                          </Typography>
                        </Tooltip>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {(post.engagement + (post.paid_likes || 0) + (post.paid_comments || 0) + (post.paid_shares || 0)).toLocaleString()}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              total
                            </Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(['likes', 'comments', 'shares'] as const).map((k, i) => (
                              <React.Fragment key={k}>
                                {i > 0 && ' · '}
                                {withBoost(post, k).toLocaleString()} {k}
                                {(post[`paid_${k}`] || 0) > 0 && ` (${post[`paid_${k}`]} from the boost)`}
                              </React.Fragment>
                            ))}
                          </Typography>
                          {/* Impressions are absent on several platforms, so say so rather
                              than printing a misleading zero. See Known Issues. */}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {withBoost(post, 'impressions')
                              ? `${withBoost(post, 'impressions').toLocaleString()} impressions`
                              : 'impressions not reported'}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>

              {/* Only when it earns its place. One page of posts needs no control. */}
              {postPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              <Pagination
                count={postPages}
                page={currentPostPage + 1}
                onChange={(_, value) => {
                  setPostPage(value - 1);
                  // Back to the section, not the top of the page — the reader is working
                  // through this list, not leaving it.
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                color="primary"
                size="small"
                siblingCount={0}
              />
              <Typography variant="caption" color="text.secondary">
                {currentPostPage * POSTS_PER_PAGE + 1}–
                {Math.min((currentPostPage + 1) * POSTS_PER_PAGE, visiblePosts.length)}
                {' of '}{visiblePosts.length}
              </Typography>
            </Box>
              )}
            </>
          )}
        </SectionCard>
      )}
    </Box>
  );
};
